import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import { getPreviewOrderStore } from './KitchenDisplay.js';
import { getTodayEmployees } from './RestaurantSchedule.js';
import type { RestaurantTable, TableStatus, Order } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

// ── Kitchen order status helpers ──────────────────────────────────────────────

function getActiveOrders(): Order[] {
  // getPreviewOrderStore initialises the shared window store with sample data if needed
  return getPreviewOrderStore('preview').filter((o) => !['delivered', 'cancelled'].includes(o.status));
}

// Match a table to an active kitchen order by name or trailing number
function getTableKitchenStatus(tableName: string): Order['status'] | null {
  const active = getActiveOrders();
  let match = active.find((o) => o.tableNumber === tableName);
  if (!match) {
    const num = tableName.match(/(\d+)$/)?.[1];
    if (num) match = active.find((o) => o.tableNumber === num);
  }
  return match?.status ?? null;
}

const KITCHEN_BADGE: Record<string, { bg: string; color: string; icon: string; label: string }> = {
  pending:   { bg: '#f59e0b22', color: '#f59e0b', icon: '⏳', label: 'Order Sent' },
  preparing: { bg: '#3b82f622', color: '#3b82f6', icon: '🍳', label: 'Preparing' },
  ready:     { bg: '#10b98122', color: '#10b981', icon: '✅', label: 'Ready to Serve!' },
};

// ── Tab store (shared window key with RestaurantTablet) ───────────────────────

interface TabItem { name: string; price: number; qty: number; emoji: string; }
interface Tab { tableName: string; guestName: string; openedAt: string; items: TabItem[]; }

function tabStore(): Record<string, Tab> {
  const w = window as unknown as { __preuve_tabs?: Record<string, Tab> };
  if (!w.__preuve_tabs) w.__preuve_tabs = {};
  return w.__preuve_tabs;
}
function getTab(name: string): Tab | null { return tabStore()[name] ?? null; }
function openTab(tableName: string, guestName: string): void {
  tabStore()[tableName] = { tableName, guestName, openedAt: new Date().toISOString(), items: [] };
}
function closeTab(name: string): void { delete tabStore()[name]; }
function tabTotal(tab: Tab): number { return tab.items.reduce((s, i) => s + i.price * i.qty, 0); }

// ── Server assignment store (persists while page is open) ─────────────────────

interface ServerAssignment { id: string; name: string; }

function serverStore(): Record<string, ServerAssignment> {
  const w = window as unknown as { __preuve_servers?: Record<string, ServerAssignment> };
  if (!w.__preuve_servers) w.__preuve_servers = {};
  return w.__preuve_servers;
}
function getAssignedServer(tableId: string): ServerAssignment | null { return serverStore()[tableId] ?? null; }
function setAssignedServer(tableId: string, assignment: ServerAssignment | null): void {
  if (assignment) { serverStore()[tableId] = assignment; }
  else { delete serverStore()[tableId]; }
}

const STATUS_COLOR: Record<string, string> = {
  available: '#10b981', occupied: '#f59e0b',
  reserved: '#3b82f6', 'needs-cleaning': '#ef4444',
};
const STATUS_LABEL: Record<string, string> = {
  available: 'Available', occupied: 'Occupied',
  reserved: 'Reserved', 'needs-cleaning': 'Needs Cleaning',
};
const STATUS_CYCLE: TableStatus[] = ['available', 'occupied', 'reserved', 'needs-cleaning'];

function seatTime(tableId: string, set?: Date): Date | null {
  const key = `preuve_seat_${tableId}`;
  if (set) { sessionStorage.setItem(key, set.toISOString()); return set; }
  const v = sessionStorage.getItem(key);
  return v ? new Date(v) : null;
}
function clearSeatTime(tableId: string) { sessionStorage.removeItem(`preuve_seat_${tableId}`); }

function elapsedLabel(since: Date): string {
  const mins = Math.floor((Date.now() - since.getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// Auto-generate a table name for a new table in a section.
// Looks at existing names in the section to infer prefix and next number.
function autoTableName(section: string, existing: RestaurantTable[]): string {
  const sectionTables = existing.filter((t) => (t.section || 'Main Floor') === section);
  // Determine prefix: "Table" for Main Floor, otherwise section name
  const prefix = section === 'Main Floor' ? 'Table' : section;
  // Find highest number used
  let max = 0;
  sectionTables.forEach((t) => {
    const match = t.name.match(/(\d+)$/);
    if (match) max = Math.max(max, parseInt(match[1]!));
  });
  return `${prefix} ${max + 1}`;
}

export async function renderRestaurantTables(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();
  const businessId = session?.businessId ?? 'preview';

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title"><i class="bi bi-grid-3x3-gap"></i> Table Management</h1>
          <p class="page-subtitle">Manage dining sections, seat parties, and track table status in real time.</p>
        </div>
        <div style="display:flex;gap:8px">
          <button class="btn btn-ghost" id="tablet-btn"><i class="bi bi-tablet"></i> Open Tablet View</button>
          <button class="btn btn-gold" id="ai-seat-btn" style="color:#fff"><i class="bi bi-stars" style="color:#fff"></i> AI Seating</button>
          <button class="btn btn-ghost" id="add-section-btn"><i class="bi bi-plus-square"></i> New Section</button>
        </div>
      </div>
    </div>

    <!-- Stats bar -->
    <div id="table-stats" style="display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:20px"></div>

    <!-- Status legend -->
    <div style="display:flex;gap:16px;margin-bottom:16px;flex-wrap:wrap;align-items:center">
      <span style="font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.5px">Status:</span>
      ${STATUS_CYCLE.map((s) => `
        <div style="display:flex;align-items:center;gap:5px;font-size:12px;color:var(--text-muted);cursor:pointer" class="status-filter-chip" data-filter="${s}">
          <div style="width:9px;height:9px;border-radius:50%;background:${STATUS_COLOR[s]}"></div>${STATUS_LABEL[s]}
        </div>`).join('')}
      <div style="display:flex;align-items:center;gap:5px;font-size:12px;color:var(--brand-gold);cursor:pointer;margin-left:4px" id="clear-filter-btn" style="display:none">
        <i class="bi bi-x-circle"></i> Clear filter
      </div>
    </div>

    <!-- New Section Modal -->
    <div id="section-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:380px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px">New Section</h3>
        <div class="form-row">
          <label>Section Name *</label>
          <input type="text" id="new-section-name" placeholder="e.g. Rooftop, Private Dining, Lounge…" />
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px">
          <button class="btn btn-ghost" id="close-section-modal">Cancel</button>
          <button class="btn btn-gold" id="save-section-btn">Create Section</button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Table Modal -->
    <div id="table-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:440px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px" id="modal-title">Add Table</h3>
        <div class="form-row-2">
          <div><label>Table Name / Number *</label><input type="text" id="table-name" placeholder="Table 1, Bar Seat A…" /></div>
          <div><label>Seats</label><input type="number" id="table-seats" value="4" min="1" max="30" /></div>
        </div>
        <div class="form-row">
          <label>Section</label>
          <select id="table-section" style="flex:1"></select>
        </div>
        <div id="new-section-row" style="display:none" class="form-row">
          <label>New Section Name</label>
          <input type="text" id="table-section-new" placeholder="e.g. Rooftop, Private Dining…" style="flex:1" />
        </div>
        <div class="form-row">
          <label>Shape</label>
          <div style="display:flex;gap:8px">
            ${[['rect', '▭ Rectangle'], ['round', '● Round'], ['bar', '▬ Bar/Counter']].map(([v, l]) =>
              `<label style="display:flex;align-items:center;gap:4px;font-size:13px;cursor:pointer">
                <input type="radio" name="table-shape" value="${v}" ${v === 'rect' ? 'checked' : ''} /> ${l}
              </label>`).join('')}
          </div>
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px">
          <button class="btn btn-ghost" id="close-modal-btn">Cancel</button>
          <button class="btn btn-gold" id="save-table-btn">Save Table</button>
        </div>
      </div>
    </div>

    <!-- Seat Party Modal -->
    <div id="seat-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:380px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:4px" id="seat-modal-title">Seat Party</h3>
        <p id="seat-modal-sub" style="font-size:12px;color:var(--text-muted);margin-bottom:16px"></p>
        <div class="form-row-2">
          <div><label>Party Size</label><input type="number" id="seat-party-size" min="1" max="30" value="2" /></div>
          <div><label>Guest Name (optional)</label><input type="text" id="seat-guest-name" placeholder="Smith party" /></div>
        </div>
        <div class="form-row">
          <label>Assign Server</label>
          <select id="seat-server">
            <option value="">— Unassigned —</option>
          </select>
        </div>
        <div class="form-row">
          <label>Notes</label>
          <input type="text" id="seat-notes" placeholder="Birthday, allergies, high chair…" />
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:8px">
          <button class="btn btn-ghost" id="close-seat-modal">Cancel</button>
          <button class="btn btn-gold" id="confirm-seat-btn"><i class="bi bi-people-fill"></i> Seat Party</button>
        </div>
      </div>
    </div>

    <div id="tables-root"></div>

    <!-- Running Receipt / Tab Modal -->
    <div id="receipt-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:1100;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:16px;padding:28px;width:440px;max-height:88vh;display:flex;flex-direction:column">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:4px">
          <div>
            <div style="font-size:18px;font-weight:800" id="receipt-title">Running Tab</div>
            <div style="font-size:12px;color:var(--text-muted)" id="receipt-meta"></div>
          </div>
          <button id="close-receipt-btn" style="background:none;border:none;font-size:20px;cursor:pointer;color:var(--text-muted);padding:0 4px">✕</button>
        </div>
        <div id="receipt-items" style="flex:1;overflow-y:auto;margin:16px 0;padding-right:4px"></div>
        <div style="border-top:1px solid var(--border);padding-top:12px">
          <div style="display:flex;justify-content:space-between;font-size:13px;color:var(--text-muted);margin-bottom:4px">
            <span>Subtotal</span><span id="receipt-subtotal"></span>
          </div>
          <div style="display:flex;justify-content:space-between;font-size:13px;color:var(--text-muted);margin-bottom:10px">
            <span>Tax (8.8%)</span><span id="receipt-tax"></span>
          </div>
          <div style="display:flex;justify-content:space-between;font-size:18px;font-weight:800;margin-bottom:16px">
            <span>Total</span><span style="color:var(--brand-gold)" id="receipt-total"></span>
          </div>
          <button class="btn btn-gold btn-lg" style="width:100%" id="receipt-pay-btn">
            <i class="bi bi-credit-card-2-front"></i> Close Tab &amp; Pay
          </button>
        </div>
      </div>
    </div>

    <!-- Tab Payment Modal -->
    <div id="tab-pay-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.78);z-index:1200;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:16px;padding:32px;width:360px;text-align:center">
        <div style="font-size:20px;font-weight:800;margin-bottom:4px">Tab Payment</div>
        <div style="font-size:13px;color:var(--text-muted);margin-bottom:8px" id="tab-pay-label"></div>
        <div style="font-size:38px;font-weight:900;color:var(--brand-gold);margin-bottom:20px" id="tab-pay-amount"></div>
        <div id="tab-pay-methods" style="display:flex;flex-direction:column;gap:8px">
          <button id="tab-tap-btn" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:14px;border-radius:12px;border:2px solid var(--brand-gold);background:transparent;color:var(--text-primary);font-size:14px;font-weight:700;cursor:pointer">
            <span style="font-size:22px">📱</span> Tap to Pay (NFC)
          </button>
          <button id="tab-card-btn" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:14px;border-radius:12px;border:2px solid var(--border);background:transparent;color:var(--text-primary);font-size:14px;font-weight:700;cursor:pointer">
            <span style="font-size:22px">💳</span> Card Insert / Swipe
          </button>
          <button id="tab-cash-btn" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:14px;border-radius:12px;border:2px solid var(--border);background:transparent;color:var(--text-primary);font-size:14px;font-weight:700;cursor:pointer">
            <span style="font-size:22px">💵</span> Cash
          </button>
        </div>
        <div id="tab-pay-processing" style="display:none;padding:20px 0">
          <div class="spinner" style="margin:0 auto 12px;width:36px;height:36px;border-width:4px"></div>
          <div style="font-size:14px;font-weight:600" id="tab-pay-msg">Processing…</div>
        </div>
        <button id="tab-pay-cancel" style="margin-top:16px;background:none;border:none;color:var(--text-muted);font-size:13px;cursor:pointer;text-decoration:underline">Cancel</button>
      </div>
    </div>

    <!-- AI Seating Modal -->
    <div id="ai-seat-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:1400;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:16px;padding:28px;width:480px;max-height:88vh;overflow-y:auto">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:4px">
          <div style="width:36px;height:36px;border-radius:50%;background:var(--brand-gold)22;border:2px solid var(--brand-gold);display:flex;align-items:center;justify-content:center;font-size:18px">✨</div>
          <div>
            <div style="font-size:17px;font-weight:800">AI Seating Assistant</div>
            <div style="font-size:11px;color:var(--text-muted)">Tell me about your party and I'll find the best available table.</div>
          </div>
          <button id="close-ai-seat-modal" style="margin-left:auto;background:none;border:none;font-size:20px;cursor:pointer;color:var(--text-muted)">✕</button>
        </div>

        <div style="border-top:1px solid var(--border);margin:16px 0"></div>

        <div class="form-row-2">
          <div>
            <label>Party Size *</label>
            <input type="number" id="ai-party-size" min="1" max="30" value="2" />
          </div>
          <div>
            <label>Guest Name</label>
            <input type="text" id="ai-guest-name" placeholder="Smith party…" />
          </div>
        </div>
        <div class="form-row-2">
          <div>
            <label>Section Preference</label>
            <select id="ai-section-pref">
              <option value="">Any section</option>
            </select>
          </div>
          <div>
            <label>Special Needs</label>
            <select id="ai-special">
              <option value="">None</option>
              <option value="highchair">High chair needed</option>
              <option value="accessibility">Accessibility / wheelchair</option>
              <option value="quiet">Quiet area preferred</option>
              <option value="window">Window / view seat</option>
            </select>
          </div>
        </div>

        <button class="btn btn-gold" id="ai-find-btn" style="width:100%;color:#fff;margin-bottom:16px">
          <i class="bi bi-stars" style="color:#fff"></i> Find Best Tables
        </button>

        <div id="ai-results" style="display:none">
          <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted);margin-bottom:10px">AI Recommendations</div>
          <div id="ai-rec-list" style="display:flex;flex-direction:column;gap:10px"></div>
        </div>

        <div id="ai-thinking" style="display:none;text-align:center;padding:24px 0">
          <div class="spinner" style="margin:0 auto 12px;width:32px;height:32px;border-width:3px"></div>
          <div style="font-size:13px;color:var(--text-muted)">Analyzing tables…</div>
        </div>

        <div id="ai-no-tables" style="display:none;text-align:center;padding:24px 0;color:var(--text-muted);font-size:13px">
          No available tables match that party size right now.
        </div>
      </div>
    </div>

    <!-- Tab Payment Success Modal -->
    <div id="tab-success-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:1300;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:2px solid #10b981;border-radius:20px;padding:48px 40px;width:340px;text-align:center">
        <div style="width:80px;height:80px;border-radius:50%;background:#10b98122;border:3px solid #10b981;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;font-size:40px">✓</div>
        <div style="font-size:26px;font-weight:900;color:#10b981;margin-bottom:6px">Payment Complete!</div>
        <div style="font-size:42px;font-weight:900;color:var(--text-primary);margin-bottom:6px" id="tab-success-amount"></div>
        <div style="font-size:13px;color:var(--text-muted);margin-bottom:28px" id="tab-success-label"></div>
        <button id="tab-success-done" class="btn btn-gold btn-lg" style="width:100%">Done</button>
      </div>
    </div>
  `;

  // Header buttons
  document.getElementById('tablet-btn')!.addEventListener('click', () => navigate('restaurant-tablet'));
  document.getElementById('ai-seat-btn')!.addEventListener('click', () => openAiSeatModal(businessId, navigate));
  document.getElementById('close-ai-seat-modal')!.addEventListener('click', () => {
    document.getElementById('ai-seat-modal')!.style.display = 'none';
  });
  document.getElementById('ai-find-btn')!.addEventListener('click', () => runAiSeating(businessId, navigate));
  document.getElementById('add-section-btn')!.addEventListener('click', openSectionModal);
  document.getElementById('close-section-modal')!.addEventListener('click', closeSectionModal);
  document.getElementById('save-section-btn')!.addEventListener('click', () => saveNewSection(businessId, navigate));

  document.getElementById('close-modal-btn')!.addEventListener('click', closeTableModal);
  document.getElementById('save-table-btn')!.addEventListener('click', () => saveTable(businessId, navigate));
  document.getElementById('close-seat-modal')!.addEventListener('click', () => {
    document.getElementById('seat-modal')!.style.display = 'none';
  });

  // Receipt modal
  document.getElementById('close-receipt-btn')!.addEventListener('click', () => {
    document.getElementById('receipt-modal')!.style.display = 'none';
  });
  document.getElementById('receipt-pay-btn')!.addEventListener('click', () => {
    document.getElementById('receipt-modal')!.style.display = 'none';
    openTabPayModal(businessId, navigate);
  });

  // Tab payment modal
  document.getElementById('tab-pay-cancel')!.addEventListener('click', () => {
    document.getElementById('tab-pay-modal')!.style.display = 'none';
  });
  ['tap', 'card', 'cash'].forEach((method) => {
    document.getElementById(`tab-${method}-btn`)!.addEventListener('click', () =>
      processTabPayment(method as 'tap' | 'card' | 'cash', businessId, navigate),
    );
  });
  document.getElementById('tab-success-done')!.addEventListener('click', () => {
    document.getElementById('tab-success-modal')!.style.display = 'none';
    renderSections(businessId, navigate, null);
  });

  // Allow Enter in section name field
  document.getElementById('new-section-name')!.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') saveNewSection(businessId, navigate);
  });

  // Status filter
  let activeFilter: string | null = null;
  document.querySelectorAll('.status-filter-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      const f = chip.getAttribute('data-filter')!;
      activeFilter = activeFilter === f ? null : f;
      renderSections(businessId, navigate, activeFilter);
    });
  });
  document.getElementById('clear-filter-btn')?.addEventListener('click', () => {
    activeFilter = null;
    renderSections(businessId, navigate, null);
  });

  // Populate server dropdown in seat modal — prefer schedule-based employees
  {
    let emps: Array<{ id: string; name: string; role: string }> = getTodayEmployees().filter((e) => e.role !== 'kitchen');
    try {
      const apiEmps = await getAPI().getEmployees(businessId) as Array<{ id: string; name: string; role: string; active: boolean }>;
      if (apiEmps.length > 0) emps = apiEmps.filter((e) => e.active && e.role !== 'kitchen');
    } catch { /* use schedule fallback */ }
    const sel = document.getElementById('seat-server') as HTMLSelectElement;
    emps.forEach((e) => {
      const opt = document.createElement('option');
      opt.value = e.id; opt.textContent = `${e.name} (${e.role})`; sel.appendChild(opt);
    });
  }

  await renderSections(businessId, navigate, null);

  // ── Live kitchen-status badges ────────────────────────────────────────────
  let lastOrderCount = getActiveOrders().length;

  function refreshKitchenBadges(): void {
    const activeOrders = getActiveOrders();
    if (activeOrders.length !== lastOrderCount) {
      lastOrderCount = activeOrders.length;
      renderSections(businessId, navigate, null);
      return;
    }
    document.querySelectorAll('[data-kitchen-badge]').forEach((el) => {
      const card = (el as HTMLElement).closest('div[style*="bg-card"], div[style*="border-top"]') as HTMLElement | null;
      const nameEl = card?.querySelector('div[style*="font-weight:800"]') as HTMLElement | null;
      const tableName = nameEl?.textContent?.trim() ?? '';
      const kitchenStatus = getTableKitchenStatus(tableName);
      const badge = kitchenStatus ? KITCHEN_BADGE[kitchenStatus] : null;
      const div = el as HTMLElement;
      if (badge) {
        div.style.cssText = `margin-top:8px;padding:6px 10px;border-radius:8px;background:${badge.bg};border:1px solid ${badge.color}44;display:flex;align-items:center;justify-content:center;gap:6px;font-size:12px;font-weight:700;color:${badge.color}`;
        div.innerHTML = `${badge.icon} ${badge.label}`;
      } else {
        div.style.display = 'none';
      }
    });
  }

  const kitchenInterval = setInterval(refreshKitchenBadges, 4000);
  window.addEventListener('kitchen:order-updated', refreshKitchenBadges);
  const cleanup = () => {
    clearInterval(kitchenInterval);
    window.removeEventListener('kitchen:order-updated', refreshKitchenBadges);
  };
  document.querySelectorAll('.nav-item').forEach((el) => el.addEventListener('click', cleanup, { once: true }));
}

// ── Section modal ─────────────────────────────────────────────────────────────

function openSectionModal(): void {
  (document.getElementById('new-section-name') as HTMLInputElement).value = '';
  document.getElementById('section-modal')!.style.display = 'flex';
  setTimeout(() => (document.getElementById('new-section-name') as HTMLInputElement).focus(), 50);
}

function closeSectionModal(): void {
  document.getElementById('section-modal')!.style.display = 'none';
}

async function saveNewSection(businessId: string, navigate: NavigateFn): Promise<void> {
  const name = (document.getElementById('new-section-name') as HTMLInputElement).value.trim();
  if (!name) { showToast('Section name required', 'error'); return; }

  // Create the first table in the new section automatically
  try {
    await getAPI().createTable({ businessId, name: `${name} 1`, seats: 4, status: 'available', section: name, shape: 'rect' } as Record<string, unknown>);
  } catch {
    previewAddTableToStore({ id: `t${Date.now()}`, businessId, name: `${name} 1`, seats: 4, status: 'available', section: name });
  }
  showToast(`Section "${name}" created`, 'success');

  closeSectionModal();
  await renderSections(businessId, navigate, null);
}

// ── Table modal ───────────────────────────────────────────────────────────────

let editingTableId: string | null = null;
let cachedTables: RestaurantTable[] = [];

function openTableModal(table?: RestaurantTable, defaultSection?: string): void {
  editingTableId = table?.id ?? null;
  (document.getElementById('modal-title') as HTMLElement).textContent = table ? 'Edit Table' : 'Add Table';
  (document.getElementById('table-name') as HTMLInputElement).value = table?.name ?? '';
  (document.getElementById('table-seats') as HTMLInputElement).value = String(table?.seats ?? 4);

  // Populate section dropdown from cached tables
  const sections = ['Main Floor', ...Array.from(
    new Set(cachedTables.map((t) => t.section || 'Main Floor').filter((s) => s !== 'Main Floor')),
  )];
  const sel = document.getElementById('table-section') as HTMLSelectElement;
  sel.innerHTML = sections.map((s) => `<option value="${escHtml(s)}">${escHtml(s)}</option>`).join('') +
    '<option value="__new__">＋ New Section…</option>';
  const target = table?.section ?? defaultSection ?? 'Main Floor';
  sel.value = sections.includes(target) ? target : 'Main Floor';

  document.getElementById('new-section-row')!.style.display = 'none';
  (document.getElementById('table-section-new') as HTMLInputElement).value = '';

  // Wire the "new section" reveal (replace listener each open to avoid duplicates)
  const newSel = sel.cloneNode(true) as HTMLSelectElement;
  sel.replaceWith(newSel);
  newSel.value = sel.value;
  newSel.addEventListener('change', () => {
    document.getElementById('new-section-row')!.style.display =
      newSel.value === '__new__' ? 'flex' : 'none';
  });

  const shape = (table as unknown as { shape?: string })?.shape ?? 'rect';
  (document.querySelector(`input[name="table-shape"][value="${shape}"]`) as HTMLInputElement)!.checked = true;
  document.getElementById('table-modal')!.style.display = 'flex';
  setTimeout(() => (document.getElementById('table-name') as HTMLInputElement).focus(), 50);
}

function closeTableModal(): void {
  document.getElementById('table-modal')!.style.display = 'none';
  editingTableId = null;
}

async function saveTable(businessId: string, navigate: NavigateFn): Promise<void> {
  const name = (document.getElementById('table-name') as HTMLInputElement).value.trim();
  const seats = parseInt((document.getElementById('table-seats') as HTMLInputElement).value) || 4;
  const selVal = (document.getElementById('table-section') as HTMLSelectElement).value;
  const section = selVal === '__new__'
    ? ((document.getElementById('table-section-new') as HTMLInputElement).value.trim() || 'Main Floor')
    : (selVal || 'Main Floor');
  const shape = (document.querySelector('input[name="table-shape"]:checked') as HTMLInputElement)?.value ?? 'rect';

  if (!name) { showToast('Table name required', 'error'); return; }
  try {
    const api = getAPI();
    if (editingTableId) {
      await api.updateTable(editingTableId, { name, seats, section, shape } as Record<string, unknown>);
    } else {
      await api.createTable({ businessId, name, seats, status: 'available', section, shape } as Record<string, unknown>);
    }
  } catch {
    if (editingTableId) {
      previewUpdateTable(editingTableId, { name, seats, section: section as string });
    } else {
      previewAddTableToStore({ id: `t${Date.now()}`, businessId, name, seats, status: 'available', section: section as string });
    }
  }
  showToast(editingTableId ? 'Table updated' : 'Table added', 'success');
  closeTableModal();
  await renderSections(businessId, navigate, null);
}

// ── Quick-add table to a specific section ─────────────────────────────────────

async function quickAddTable(
  section: string,
  allTables: RestaurantTable[],
  businessId: string,
  navigate: NavigateFn,
  filter: string | null,
): Promise<void> {
  const name = autoTableName(section, allTables);
  try {
    await getAPI().createTable({ businessId, name, seats: 4, status: 'available', section, shape: 'rect' } as Record<string, unknown>);
  } catch {
    previewAddTableToStore({ id: `t${Date.now()}`, businessId, name, seats: 4, status: 'available', section });
  }
  showToast(`Added ${name}`, 'success');
  await renderSections(businessId, navigate, filter);
}

// ── Sections renderer ─────────────────────────────────────────────────────────

async function renderSections(businessId: string, navigate: NavigateFn, filter: string | null): Promise<void> {
  let tables: RestaurantTable[] = [];
  try {
    tables = await getAPI().getTables(businessId) as RestaurantTable[];
  } catch {
    tables = getPreviewTableStore(businessId);
  }
  cachedTables = tables;

  const filtered = filter ? tables.filter((t) => t.status === filter) : tables;

  // Stats bar
  const stats = {
    total: tables.length,
    available: tables.filter((t) => t.status === 'available').length,
    occupied: tables.filter((t) => t.status === 'occupied').length,
    reserved: tables.filter((t) => t.status === 'reserved').length,
  };
  const occupancyPct = tables.length ? Math.round((stats.occupied / tables.length) * 100) : 0;
  document.getElementById('table-stats')!.innerHTML = [
    statPill(`${stats.total}`, 'Total Tables', 'bi-grid', 'var(--text-secondary)'),
    statPill(`${stats.available}`, 'Available', 'bi-circle-fill', STATUS_COLOR.available),
    statPill(`${stats.occupied}`, 'Occupied', 'bi-people-fill', STATUS_COLOR.occupied),
    statPill(`${stats.reserved}`, 'Reserved', 'bi-clock-fill', STATUS_COLOR.reserved),
    statPill(`${occupancyPct}%`, 'Occupancy', 'bi-graph-up', 'var(--brand-gold)'),
  ].join('');

  // Group by section
  const sections = new Map<string, RestaurantTable[]>();
  filtered.forEach((t) => {
    const s = t.section || 'Main Floor';
    if (!sections.has(s)) sections.set(s, []);
    sections.get(s)!.push(t);
  });

  const root = document.getElementById('tables-root')!;

  if (filtered.length === 0) {
    root.innerHTML = `<div class="empty-state"><div class="empty-state-icon"><i class="bi bi-grid-3x3-gap" style="font-size:48px"></i></div><div class="empty-state-title">${filter ? `No ${STATUS_LABEL[filter]} tables` : 'No tables yet'}</div><div class="empty-state-desc">Use "New Section" to create your first section.</div></div>`;
    return;
  }

  root.innerHTML = [...sections.entries()].map(([section, tbls]) => `
    <div style="margin-bottom:28px" data-section="${escHtml(section)}">
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px">
        <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:var(--text-muted)">${escHtml(section)}</div>
        <div style="flex:1;height:1px;background:var(--border)"></div>
        <div style="font-size:11px;color:var(--text-muted)">${tbls.length} table${tbls.length !== 1 ? 's' : ''}</div>
        <button class="btn btn-ghost btn-sm" data-quick-add="${escHtml(section)}" style="font-size:11px;padding:3px 10px;display:flex;align-items:center;gap:4px" title="Add table to ${escHtml(section)}">
          <i class="bi bi-plus-lg"></i> Add Table
        </button>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:14px">
        ${tbls.map((t) => renderTableCard(t)).join('')}
      </div>
    </div>
  `).join('');

  // Wire + (quick-add) buttons
  root.querySelectorAll('[data-quick-add]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const section = (btn as HTMLElement).dataset['quickAdd']!;
      quickAddTable(section, tables, businessId, navigate, filter);
    });
  });

  // Wire status select
  root.querySelectorAll('[data-status-select]').forEach((sel) => {
    sel.addEventListener('change', async (e) => {
      e.stopPropagation();
      const id = (sel as HTMLElement).dataset['statusSelect']!;
      const next = (sel as HTMLSelectElement).value as TableStatus;
      const current = (sel as HTMLElement).dataset['current'] as TableStatus;
      if (next === 'occupied' && current !== 'occupied') {
        const table = tables.find((t) => t.id === id);
        if (table) { openSeatModal(table, businessId, navigate, filter); return; }
      }
      if (next !== 'occupied') clearSeatTime(id);
      if (next === 'available') setAssignedServer(id, null);
      try { await getAPI().updateTable(id, { status: next }); } catch { previewUpdateTable(id, { status: next }); }
      await renderSections(businessId, navigate, filter);
    });
  });

  // Wire server assignment select
  root.querySelectorAll('[data-server-select]').forEach((sel) => {
    sel.addEventListener('change', (e) => {
      e.stopPropagation();
      const id = (sel as HTMLElement).dataset['serverSelect']!;
      const opt = (sel as HTMLSelectElement).selectedOptions[0];
      if (opt?.value) {
        setAssignedServer(id, { id: opt.value, name: opt.dataset['name'] ?? opt.text });
        showToast(`Server assigned`, 'success');
      } else {
        setAssignedServer(id, null);
      }
      // Re-render just this card's subtitle without a full refresh
      renderSections(businessId, navigate, filter);
    });
  });

  root.querySelectorAll('[data-seat-id]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset['seatId']!;
      const table = tables.find((t) => t.id === id);
      if (!table) return;
      openSeatModal(table, businessId, navigate, filter);
    });
  });

  root.querySelectorAll('[data-edit-id]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset['editId']!;
      const table = tables.find((t) => t.id === id);
      if (table) openTableModal(table);
    });
  });

  root.querySelectorAll('[data-order-id]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const name = (btn as HTMLElement).dataset['orderName']!;
      navigate('restaurant-tablet', { tablePreset: name });
    });
  });

  root.querySelectorAll('[data-delete-id]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset['deleteId']!;
      if (!confirm('Remove this table?')) return;
      try { await getAPI().deleteTable(id); } catch { previewDeleteTableFromStore(id); }
      await renderSections(businessId, navigate, filter);
    });
  });

  // View Receipt buttons on tab badge
  root.querySelectorAll('[data-receipt-name]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const name = (btn as HTMLElement).dataset['receiptName']!;
      openReceiptModal(name, businessId, navigate);
    });
  });
}

function renderTableCard(t: RestaurantTable): string {
  const color = STATUS_COLOR[t.status] ?? '#64748b';
  const label = STATUS_LABEL[t.status] ?? t.status;
  const seated = t.status === 'occupied' ? seatTime(t.id) : null;
  const shape = (t as unknown as { shape?: string }).shape ?? 'rect';
  const tableIcon = shape === 'round'
    ? `<div style="width:52px;height:52px;border-radius:50%;background:${color}22;border:2px solid ${color};display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:${color}">${t.seats}</div>`
    : shape === 'bar'
    ? `<div style="width:64px;height:28px;border-radius:4px;background:${color}22;border:2px solid ${color};display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:${color}">${t.seats}</div>`
    : `<div style="width:56px;height:40px;border-radius:6px;background:${color}22;border:2px solid ${color};display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:${color}">${t.seats}</div>`;

  const kitchenStatus = getTableKitchenStatus(t.name);
  const badge = kitchenStatus ? KITCHEN_BADGE[kitchenStatus] : null;
  const kitchenBanner = badge
    ? `<div data-kitchen-badge="${escHtml(t.id)}" style="margin-top:6px;padding:5px 10px;border-radius:8px;background:${badge.bg};border:1px solid ${badge.color}44;display:flex;align-items:center;justify-content:center;gap:6px;font-size:12px;font-weight:700;color:${badge.color}">
        ${badge.icon} ${badge.label}
      </div>`
    : `<div data-kitchen-badge="${escHtml(t.id)}" style="display:none"></div>`;

  const tab = getTab(t.name);
  const tabBanner = tab
    ? `<div style="margin-top:6px;padding:5px 10px;border-radius:8px;background:#3b82f622;border:1px solid #3b82f644;display:flex;align-items:center;justify-content:space-between;font-size:12px;font-weight:700;color:#3b82f6">
        <span>📋 Tab: $${tabTotal(tab).toFixed(2)}</span>
        <button data-receipt-id="${escHtml(t.id)}" data-receipt-name="${escHtml(t.name)}"
          style="background:#3b82f622;border:1px solid #3b82f6;border-radius:5px;color:#3b82f6;font-size:10px;font-weight:700;padding:2px 7px;cursor:pointer">
          View Receipt
        </button>
      </div>`
    : '';

  const server = getAssignedServer(t.id);
  const todayStaff = getTodayEmployees().filter((e) => e.role !== 'kitchen');
  const serverSelect = `
    <select data-server-select="${escHtml(t.id)}"
      style="width:100%;margin-top:6px;padding:4px 6px;border-radius:8px;border:1px solid var(--border);background:var(--bg-secondary);color:var(--text-primary);font-size:11px;cursor:pointer">
      <option value="">— Assign Server —</option>
      ${todayStaff.map((e) => `<option value="${escHtml(e.id)}" data-name="${escHtml(e.name)}" ${server?.id === e.id ? 'selected' : ''}>${escHtml(e.name)} (${escHtml(e.role)})</option>`).join('')}
    </select>`;

  return `
    <div style="background:var(--bg-card);border:1px solid var(--border);border-top:3px solid ${color};border-radius:10px;padding:14px;position:relative">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:10px">
        <div>
          <div style="font-size:16px;font-weight:800;line-height:1">${escHtml(t.name)}</div>
          <div style="font-size:10px;color:var(--text-muted);margin-top:2px">
            ${t.seats} seats${server ? ` · <span style="color:var(--brand-gold)">${escHtml(server.name)}</span>` : ''}
            ${seated ? ` · ${elapsedLabel(seated)}` : ''}
          </div>
        </div>
        ${tableIcon}
      </div>

      <!-- Status select (replaces cycle button) -->
      <select data-status-select="${escHtml(t.id)}" data-current="${t.status}"
        style="width:100%;margin-bottom:8px;padding:5px 8px;border-radius:20px;border:none;background:${color}18;color:${color};font-size:11px;font-weight:700;cursor:pointer">
        ${STATUS_CYCLE.map((s) => `<option value="${s}" ${s === t.status ? 'selected' : ''}>${STATUS_LABEL[s]}</option>`).join('')}
      </select>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px">
        ${t.status === 'available' || t.status === 'reserved'
          ? `<button class="btn btn-gold btn-sm" data-seat-id="${t.id}" style="font-size:11px"><i class="bi bi-people"></i> Seat</button>`
          : `<button class="btn btn-ghost btn-sm" data-order-id="${t.id}" data-order-name="${escHtml(t.name)}" style="font-size:11px"><i class="bi bi-receipt"></i> Order</button>`
        }
        <button class="btn btn-ghost btn-sm" data-edit-id="${t.id}" style="font-size:11px"><i class="bi bi-pencil"></i> Edit</button>
      </div>

      ${t.status === 'occupied' ? serverSelect : ''}
      ${kitchenBanner}
      ${tabBanner}

      <button data-delete-id="${t.id}" style="position:absolute;top:8px;right:8px;background:none;border:none;cursor:pointer;color:var(--text-muted);font-size:13px;padding:2px 4px;opacity:.5" title="Remove table">✕</button>
    </div>
  `;
}

function openSeatModal(table: RestaurantTable, businessId: string, navigate: NavigateFn, filter: string | null): void {
  (document.getElementById('seat-modal-title') as HTMLElement).textContent = `Seat Party — ${table.name}`;
  (document.getElementById('seat-modal-sub') as HTMLElement).textContent = `${table.seats} seats · ${table.section ?? 'Main Floor'}`;
  (document.getElementById('seat-party-size') as HTMLInputElement).value = '2';
  (document.getElementById('seat-guest-name') as HTMLInputElement).value = '';
  (document.getElementById('seat-notes') as HTMLInputElement).value = '';
  (document.getElementById('seat-server') as HTMLSelectElement).value = '';

  const confirmBtn = document.getElementById('confirm-seat-btn')!;
  const newBtn = confirmBtn.cloneNode(true) as HTMLElement;
  confirmBtn.replaceWith(newBtn);
  newBtn.addEventListener('click', async () => {
    const guestName = (document.getElementById('seat-guest-name') as HTMLInputElement).value.trim();
    seatTime(table.id, new Date());
    openTab(table.name, guestName || 'Guest');
    try { await getAPI().updateTable(table.id, { status: 'occupied' }); } catch { previewUpdateTable(table.id, { status: 'occupied' }); }
    showToast(`${table.name} seated — tab opened`, 'success');
    document.getElementById('seat-modal')!.style.display = 'none';
    await renderSections(businessId, navigate, filter);
  });

  document.getElementById('seat-modal')!.style.display = 'flex';
}

// ── Receipt & tab payment ─────────────────────────────────────────────────────

let activeReceiptTableName = '';

function openReceiptModal(tableName: string, businessId: string, navigate: NavigateFn): void {
  const tab = getTab(tableName);
  activeReceiptTableName = tableName;
  if (!tab) {
    showToast('No open tab for this table', 'error');
    return;
  }
  const subtotal = tabTotal(tab);
  const tax = subtotal * 0.088;
  const total = subtotal + tax;

  (document.getElementById('receipt-title') as HTMLElement).textContent = `Running Tab — ${tableName}`;
  (document.getElementById('receipt-meta') as HTMLElement).textContent =
    `${tab.guestName || 'Guest'} · Opened ${new Date(tab.openedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  document.getElementById('receipt-items')!.innerHTML = tab.items.length
    ? tab.items.map((i) => `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid var(--border)40">
          <div style="display:flex;align-items:center;gap:8px">
            <span style="font-size:18px">${i.emoji}</span>
            <div>
              <div style="font-size:13px;font-weight:600">${escHtml(i.name)}</div>
              <div style="font-size:11px;color:var(--text-muted)">$${i.price.toFixed(2)} each</div>
            </div>
          </div>
          <div style="text-align:right">
            <div style="font-size:13px;color:var(--text-muted)">×${i.qty}</div>
            <div style="font-size:13px;font-weight:700">$${(i.price * i.qty).toFixed(2)}</div>
          </div>
        </div>`).join('')
    : '<div style="color:var(--text-muted);text-align:center;padding:24px;font-size:13px">No items on tab yet.<br>Send orders from the tablet.</div>';

  (document.getElementById('receipt-subtotal') as HTMLElement).textContent = `$${subtotal.toFixed(2)}`;
  (document.getElementById('receipt-tax') as HTMLElement).textContent = `$${tax.toFixed(2)}`;
  (document.getElementById('receipt-total') as HTMLElement).textContent = `$${total.toFixed(2)}`;

  document.getElementById('receipt-modal')!.style.display = 'flex';
}

function openTabPayModal(businessId: string, navigate: NavigateFn): void {
  const tab = getTab(activeReceiptTableName);
  if (!tab) return;
  const subtotal = tabTotal(tab);
  const total = subtotal * 1.088;
  (document.getElementById('tab-pay-label') as HTMLElement).textContent =
    `${activeReceiptTableName} · ${tab.guestName || 'Guest'}`;
  (document.getElementById('tab-pay-amount') as HTMLElement).textContent = `$${total.toFixed(2)}`;
  document.getElementById('tab-pay-methods')!.style.display = 'flex';
  document.getElementById('tab-pay-processing')!.style.display = 'none';
  (document.getElementById('tab-pay-cancel') as HTMLButtonElement).style.display = 'block';
  document.getElementById('tab-pay-modal')!.style.display = 'flex';
}

function processTabPayment(method: 'tap' | 'card' | 'cash', businessId: string, navigate: NavigateFn): void {
  const msgs: Record<string, string> = {
    tap: '📱 Hold card or phone near reader…',
    card: '💳 Insert or swipe card…',
    cash: '💵 Processing cash payment…',
  };
  document.getElementById('tab-pay-methods')!.style.display = 'none';
  (document.getElementById('tab-pay-cancel') as HTMLButtonElement).style.display = 'none';
  document.getElementById('tab-pay-processing')!.style.display = 'block';
  (document.getElementById('tab-pay-msg') as HTMLElement).textContent = msgs[method] ?? 'Processing…';

  setTimeout(async () => {
    document.getElementById('tab-pay-modal')!.style.display = 'none';
    const total = (document.getElementById('tab-pay-amount') as HTMLElement).textContent ?? '$0.00';
    (document.getElementById('tab-success-amount') as HTMLElement).textContent = total;
    (document.getElementById('tab-success-label') as HTMLElement).textContent =
      `${activeReceiptTableName} · Tab Closed`;
    document.getElementById('tab-success-modal')!.style.display = 'flex';
    // Close the tab and mark table available
    closeTab(activeReceiptTableName);
    try {
      const tables = await getAPI().getTables(businessId) as RestaurantTable[];
      const table = tables.find((t) => t.name === activeReceiptTableName);
      if (table) await getAPI().updateTable(table.id, { status: 'available' });
    } catch {
      const t = getPreviewTableStore(businessId).find((t) => t.name === activeReceiptTableName);
      if (t) previewUpdateTable(t.id, { status: 'available' });
    }
  }, method === 'cash' ? 700 : 1800);
}

function statPill(value: string, label: string, icon: string, color: string): string {
  return `
    <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:8px;padding:12px 14px;display:flex;align-items:center;gap:10px">
      <i class="bi ${icon}" style="color:${color};font-size:18px;flex-shrink:0"></i>
      <div><div style="font-size:18px;font-weight:700;color:var(--text-primary)">${value}</div><div style="font-size:10px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.5px">${label}</div></div>
    </div>
  `;
}

// ── AI Seating ────────────────────────────────────────────────────────────────

function openAiSeatModal(businessId: string, navigate: NavigateFn): void {
  // Populate section preference dropdown from live table data
  const sections = Array.from(new Set(
    getPreviewTableStore(businessId).map((t) => t.section || 'Main Floor')
  ));
  const sel = document.getElementById('ai-section-pref') as HTMLSelectElement;
  sel.innerHTML = '<option value="">Any section</option>' +
    sections.map((s) => `<option value="${escHtml(s)}">${escHtml(s)}</option>`).join('');

  (document.getElementById('ai-party-size') as HTMLInputElement).value = '2';
  (document.getElementById('ai-guest-name') as HTMLInputElement).value = '';
  (document.getElementById('ai-special') as HTMLSelectElement).value = '';
  document.getElementById('ai-results')!.style.display = 'none';
  document.getElementById('ai-thinking')!.style.display = 'none';
  document.getElementById('ai-no-tables')!.style.display = 'none';
  document.getElementById('ai-seat-modal')!.style.display = 'flex';
  setTimeout(() => (document.getElementById('ai-party-size') as HTMLInputElement).focus(), 50);
}

interface AiRec { table: RestaurantTable; score: number; reasons: string[]; server: string | null; }

function runAiSeating(businessId: string, navigate: NavigateFn): void {
  const partySize = parseInt((document.getElementById('ai-party-size') as HTMLInputElement).value) || 1;
  const guestName = (document.getElementById('ai-guest-name') as HTMLInputElement).value.trim();
  const sectionPref = (document.getElementById('ai-section-pref') as HTMLSelectElement).value;
  const special = (document.getElementById('ai-special') as HTMLSelectElement).value;

  document.getElementById('ai-thinking')!.style.display = 'block';
  document.getElementById('ai-results')!.style.display = 'none';
  document.getElementById('ai-no-tables')!.style.display = 'none';

  // Simulate brief AI "thinking" delay
  setTimeout(() => {
    document.getElementById('ai-thinking')!.style.display = 'none';

    const all = getPreviewTableStore(businessId);
    const available = all.filter((t) => t.status === 'available' || t.status === 'reserved');
    const fits = available.filter((t) => t.seats >= partySize);

    if (fits.length === 0) {
      document.getElementById('ai-no-tables')!.style.display = 'block';
      return;
    }

    // Count occupied tables per section for server-load balancing
    const sectionLoad: Record<string, number> = {};
    all.filter((t) => t.status === 'occupied').forEach((t) => {
      const s = t.section || 'Main Floor';
      sectionLoad[s] = (sectionLoad[s] ?? 0) + 1;
    });

    // Score each available table
    const recs: AiRec[] = fits.map((t) => {
      const section = t.section || 'Main Floor';
      const reasons: string[] = [];
      let score = 100;

      // Capacity fit — penalise oversized tables
      const slack = t.seats - partySize;
      if (slack === 0) { score += 20; reasons.push(`Perfect ${t.seats}-seat fit for ${partySize} guests`); }
      else if (slack <= 2) { score += 10; reasons.push(`${t.seats} seats — comfortable for ${partySize}`); }
      else { score -= slack * 3; reasons.push(`${t.seats} seats — slightly larger than needed`); }

      // Section preference
      if (sectionPref && section === sectionPref) { score += 15; reasons.push(`Preferred section: ${section}`); }
      else if (sectionPref && section !== sectionPref) score -= 10;

      // Server load — prefer less busy sections
      const load = sectionLoad[section] ?? 0;
      if (load === 0) { score += 12; reasons.push('Server available in this section'); }
      else if (load <= 2) { score += 5; reasons.push('Low server workload'); }
      else { score -= load * 2; reasons.push('Section is busy'); }

      // Special needs
      if (special === 'quiet' && section !== 'Bar') { score += 8; reasons.push('Quieter section'); }
      if (special === 'accessibility') { score += 5; reasons.push('Ground-floor access'); }
      if (special === 'window' && section === 'Patio') { score += 12; reasons.push('Outdoor / patio view'); }
      if (special === 'highchair' && t.seats >= 4) { score += 5; reasons.push('Enough room for high chair'); }

      // Reserved tables slightly lower priority
      if (t.status === 'reserved') score -= 5;

      // Assign a server from today's schedule for this section
      const staff = getTodayEmployees().filter((e) => e.role === 'server' || e.role === 'bartender');
      const sectionAssigned = Object.values(serverStore())
        .filter((a) => {
          const tbl = all.find((x) => x.id === Object.keys(serverStore()).find((k) => serverStore()[k] === a));
          return tbl && (tbl.section || 'Main Floor') === section;
        });
      const assignedIds = sectionAssigned.map((a) => a.id);
      const unloaded = staff.find((e) => !assignedIds.includes(e.id));
      const server = unloaded ?? staff[0] ?? null;

      return { table: t, score, reasons: reasons.slice(0, 3), server: server?.name ?? null };
    });

    recs.sort((a, b) => b.score - a.score);
    const top = recs.slice(0, 3);

    document.getElementById('ai-rec-list')!.innerHTML = top.map((rec, i) => {
      const t = rec.table;
      const rankLabel = i === 0 ? '🥇 Best Match' : i === 1 ? '🥈 Good Option' : '🥉 Alternative';
      const rankBorder = i === 0 ? 'var(--brand-gold)' : i === 1 ? '#94a3b8' : '#64748b';
      return `
        <div style="border:2px solid ${rankBorder};border-radius:12px;padding:14px;background:var(--bg-secondary)">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
            <div>
              <span style="font-size:11px;font-weight:700;color:${rankBorder}">${rankLabel}</span>
              <div style="font-size:16px;font-weight:800;margin-top:2px">${escHtml(t.name)}</div>
              <div style="font-size:11px;color:var(--text-muted)">${t.seats} seats · ${escHtml(t.section || 'Main Floor')}</div>
            </div>
            <button data-ai-select="${escHtml(t.id)}" data-ai-guest="${escHtml(guestName)}" data-ai-server="${escHtml(rec.server ?? '')}"
              class="btn btn-gold btn-sm" style="color:#fff;white-space:nowrap">
              Seat Here
            </button>
          </div>
          <div style="display:flex;flex-direction:column;gap:3px">
            ${rec.reasons.map((r) => `<div style="font-size:11px;color:var(--text-muted);display:flex;align-items:center;gap:5px"><span style="color:var(--brand-gold)">✓</span>${escHtml(r)}</div>`).join('')}
            ${rec.server ? `<div style="font-size:11px;color:var(--text-muted);display:flex;align-items:center;gap:5px"><span style="color:#3b82f6">👤</span>Suggested server: <strong style="color:var(--text-primary)">${escHtml(rec.server)}</strong></div>` : ''}
          </div>
        </div>
      `;
    }).join('');

    document.getElementById('ai-results')!.style.display = 'block';

    // Wire "Seat Here" buttons
    document.querySelectorAll('[data-ai-select]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset['aiSelect']!;
        const guest = (btn as HTMLElement).dataset['aiGuest']!;
        const serverName = (btn as HTMLElement).dataset['aiServer']!;
        const table = getPreviewTableStore(businessId).find((t) => t.id === id);
        if (!table) return;
        document.getElementById('ai-seat-modal')!.style.display = 'none';
        // Pre-fill seat modal
        openSeatModal(table, businessId, navigate, null);
        setTimeout(() => {
          if (guest) (document.getElementById('seat-guest-name') as HTMLInputElement).value = guest;
          if (serverName) {
            const serverSel = document.getElementById('seat-server') as HTMLSelectElement;
            const opt = Array.from(serverSel.options).find((o) => o.text.startsWith(serverName));
            if (opt) serverSel.value = opt.value;
          }
        }, 30);
      });
    });
  }, 900);
}

const SEED_TABLES: Omit<RestaurantTable, 'businessId'>[] = [
  { id: 't1', name: 'Table 1', seats: 4, status: 'available',       section: 'Main Floor' },
  { id: 't2', name: 'Table 2', seats: 4, status: 'occupied',        section: 'Main Floor' },
  { id: 't3', name: 'Table 3', seats: 6, status: 'available',       section: 'Main Floor' },
  { id: 't4', name: 'Table 4', seats: 2, status: 'reserved',        section: 'Main Floor' },
  { id: 't5', name: 'Table 5', seats: 8, status: 'needs-cleaning',  section: 'Main Floor' },
  { id: 't6', name: 'Table 6', seats: 4, status: 'available',       section: 'Main Floor' },
  { id: 't7', name: 'Bar 1',   seats: 2, status: 'occupied',        section: 'Bar' },
  { id: 't8', name: 'Bar 2',   seats: 2, status: 'available',       section: 'Bar' },
  { id: 't9', name: 'Bar 3',   seats: 2, status: 'occupied',        section: 'Bar' },
  { id: 'ta', name: 'Patio A', seats: 4, status: 'available',       section: 'Patio' },
  { id: 'tb', name: 'Patio B', seats: 6, status: 'reserved',        section: 'Patio' },
  { id: 'tc', name: 'Patio C', seats: 4, status: 'available',       section: 'Patio' },
];

export function getPreviewTableStore(businessId: string): RestaurantTable[] {
  const w = window as unknown as { __preuve_tables?: RestaurantTable[] };
  if (!w.__preuve_tables) {
    w.__preuve_tables = SEED_TABLES.map((t) => ({ ...t, businessId }));
  }
  return w.__preuve_tables;
}

function previewUpdateTable(id: string, changes: Partial<RestaurantTable>): void {
  const w = window as unknown as { __preuve_tables?: RestaurantTable[] };
  const t = w.__preuve_tables?.find((t) => t.id === id);
  if (t) Object.assign(t, changes);
}

function previewAddTableToStore(table: RestaurantTable): void {
  const w = window as unknown as { __preuve_tables?: RestaurantTable[] };
  if (!w.__preuve_tables) getPreviewTableStore(table.businessId);
  w.__preuve_tables!.push(table);
}

function previewDeleteTableFromStore(id: string): void {
  const w = window as unknown as { __preuve_tables?: RestaurantTable[] };
  if (w.__preuve_tables) w.__preuve_tables = w.__preuve_tables.filter((t) => t.id !== id);
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
